import { BASE_BACKEND_URL, fetchWithAuth } from '../../../auth'
import { useEffect, useRef, useState } from 'react'

import { DEFAULT_PER_PAGE } from '../../../constants/constant'

const STORE_RELATIONS_HISTORY_DEFAULT_DATA = {
  results: [],
  total_records: 0
}

const useFetchStoreHistory = (storeRelationId, { enabled = false, page = 1, perPage = DEFAULT_PER_PAGE } = {}) => {
  const abortControllerRef = useRef(null)
  const cacheRef = useRef(new Map())
  const [data, setData] = useState(STORE_RELATIONS_HISTORY_DEFAULT_DATA)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!storeRelationId) {
      setData(STORE_RELATIONS_HISTORY_DEFAULT_DATA)
      setError(null)
      setLoading(false)
      return undefined
    }

    if (!enabled) {
      setError(null)
      setLoading(false)
      return undefined
    }

    const cacheKey = `${storeRelationId}-${page}-${perPage}`
    const cachedResponse = cacheRef.current.get(cacheKey)

    if (cachedResponse) {
      setData(cachedResponse)
      setError(null)
      setLoading(false)
      return undefined
    }

    abortControllerRef.current?.abort()

    const controller = new AbortController()
    abortControllerRef.current = controller

    const fetchStoreHistory = async () => {
      try {
        setData((currentData) => ({
          ...currentData,
          results: []
        }))
        setLoading(true)
        setError(null)

        const response = await fetchWithAuth(
          `${BASE_BACKEND_URL}/poc/store_relations_overview/${storeRelationId}/history?page=${page}&per_page=${perPage}`,
          {
            signal: controller.signal
          }
        )

        const normalizedResponse = response ?? STORE_RELATIONS_HISTORY_DEFAULT_DATA

        cacheRef.current.set(cacheKey, normalizedResponse)
        setData(normalizedResponse)
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('fetchStoreHistory error', err)
          setError(err)
        }
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null
          setLoading(false)
        }
      }
    }

    fetchStoreHistory()

    return () => {
      controller.abort()
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null
      }
    }
  }, [enabled, page, perPage, storeRelationId])

  return {
    data,
    loading,
    error
  }
}

export default useFetchStoreHistory
